import { Flex, Input, Modal, Segmented } from 'antd'
import { Image, LayoutGrid, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Empty } from 'src/components/states/Empty'
import { TabCountTag } from 'src/components/TabCountTag'
import { AVATAR_PALETTE } from 'src/components/UserCell/avatarColor'
import { useDebouncedValue, useIconSizes } from 'src/hooks'

import { getIconCatalog } from './catalog'
import { IconPickerColorField } from './IconPickerColorField'
import { IconPickerGrid } from './IconPickerGrid'
import { IconPickerPhotoPanel } from './IconPickerPhotoPanel'
import { useStyles } from './styles'
import type {
  IconPickerPhotoConfig,
  IconPickerValue,
  PickedPhoto
} from './types'

const SEARCH_DEBOUNCE_MS = 150
const MODAL_WIDTH = 760
const DEFAULT_COLOR = AVATAR_PALETTE[0]

type Tab = 'icons' | 'photo'

type Draft = {
  tab: Tab
  name: string | null
  color: string
  photo: PickedPhoto | null
}

const draftFrom = (value: IconPickerValue | null | undefined): Draft => ({
  tab: value?.kind === 'photo' ? 'photo' : 'icons',
  name: value?.kind === 'lucide' ? value.name : null,
  color: value?.kind === 'lucide' ? value.color : DEFAULT_COLOR,
  photo: value?.kind === 'photo' ? value : null
})

const draftToValue = (draft: Draft): IconPickerValue | null => {
  if (draft.tab === 'photo') {
    return draft.photo
  }

  return draft.name
    ? { kind: 'lucide', name: draft.name, color: draft.color }
    : null
}

type IconPickerModalProps = {
  open: boolean
  value?: IconPickerValue | null
  onSelect: (value: IconPickerValue) => void
  onCancel: () => void
  /** Enables the "own photo" tab. Omit for a Lucide-only picker. */
  photo?: IconPickerPhotoConfig
}

export const IconPickerModal = ({
  open,
  value,
  onSelect,
  onCancel,
  photo
}: IconPickerModalProps) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const iconSizes = useIconSizes()

  const [draft, setDraft] = useState(() => draftFrom(value))
  const [search, setSearch] = useState('')
  const query = useDebouncedValue(search, SEARCH_DEBOUNCE_MS)

  const entries = useMemo(() => {
    if (!open) return []

    const all = getIconCatalog().entries
    const normalized = query.trim().toLowerCase().replace(/\s+/g, '-')

    return normalized
      ? all.filter(({ name }) => name.includes(normalized))
      : all
  }, [open, query])

  const activeTab: Tab = photo ? draft.tab : 'icons'
  const showIcons = activeTab === 'icons'
  const selected = draftToValue({ ...draft, tab: activeTab })

  const confirm = (next: IconPickerValue) => {
    setSearch('')
    onSelect(next)
  }

  const handleCancel = () => {
    setDraft(draftFrom(value))
    setSearch('')
    onCancel()
  }

  return (
    <Modal
      open={open}
      width={MODAL_WIDTH}
      centered
      className={styles.modal}
      title={
        <Flex align="center">
          {showIcons ? t('iconPicker.title') : t('iconPicker.titlePhoto')}
          {showIcons && <TabCountTag value={entries.length} />}
        </Flex>
      }
      okText={t('iconPicker.select')}
      okButtonProps={{ disabled: !selected }}
      onOk={() => selected && confirm(selected)}
      onCancel={handleCancel}
      destroyOnHidden
    >
      <Flex vertical gap={12}>
        {photo && (
          <Segmented<Tab>
            className={styles.tabs}
            value={activeTab}
            onChange={(tab) => setDraft((current) => ({ ...current, tab }))}
            options={[
              {
                value: 'icons',
                label: t('iconPicker.tabIcons'),
                icon: <LayoutGrid size={iconSizes.small} />
              },
              {
                value: 'photo',
                label: t('iconPicker.tabPhoto'),
                icon: <Image size={iconSizes.small} />
              }
            ]}
          />
        )}

        {showIcons && (
          <>
            <Input
              value={search}
              allowClear
              prefix={<Search size={iconSizes.small} />}
              placeholder={t('iconPicker.searchPlaceholder')}
              onChange={(event) => setSearch(event.target.value)}
            />

            <IconPickerColorField
              value={draft.color}
              onChange={(color) =>
                setDraft((current) => ({ ...current, color }))
              }
            />

            {entries.length === 0 ? (
              <Flex
                className={styles.placeholder}
                align="center"
                justify="center"
              >
                <Empty
                  title={t('iconPicker.emptyTitle')}
                  description={t('iconPicker.emptyDescription')}
                />
              </Flex>
            ) : (
              <IconPickerGrid
                entries={entries}
                color={draft.color}
                selectedName={draft.name}
                onSelect={(name) =>
                  setDraft((current) => ({ ...current, name }))
                }
                onConfirm={(name) =>
                  confirm({ kind: 'lucide', name, color: draft.color })
                }
              />
            )}
          </>
        )}

        {photo && !showIcons && (
          <IconPickerPhotoPanel
            config={photo}
            value={draft.photo}
            onChange={(next) =>
              setDraft((current) => ({ ...current, photo: next }))
            }
          />
        )}
      </Flex>
    </Modal>
  )
}
